import { Button, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useGetDashboard } from '@/api/REST/dashboards';
import { routes } from '@/constants/routes';
import { useNotification } from '@/hooks/useNotification';
import { DashboardCreatedProperty } from '@/pages/reporting/Dashboard/components/DashboardCreatedProperty';
import { DashboardMetaProperties } from '@/pages/reporting/Dashboard/components/DashboardMetaProperties';

export const DashboardDetails = ({ dashboardId }: { dashboardId: string }) => {
  const { t } = useTranslation('dashboardsOverview');
  const { showError } = useNotification();

  const {
    data: dashboardData,
    isLoading,
    isError,
  } = useGetDashboard(Number(dashboardId));

  if (isLoading) {
    return (
      <Stack gap={5}>
        <Stack gap={2}>
          <Skeleton variant="rounded" width="90%" height={18} />
          <Stack direction="row" gap={0.5} alignItems="center">
            {[0, 1].map((index) => (
              <Skeleton key={index} variant="rounded" width={80} height={12} />
            ))}
          </Stack>
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="end">
          <Skeleton variant="pilled" width={120} height={24} />
        </Stack>
      </Stack>
    );
  }

  if (!dashboardData || isError) {
    showError({
      message: t('DashboardDetailsError'),
    });

    return (
      <Typography color="error" textAlign="center">
        {t('DashboardDetailsError')}
      </Typography>
    );
  }

  return (
    <Stack gap={5}>
      <Stack gap={2}>
        <Typography component="h2" variant="h3SemiBold" color="primary">
          {dashboardData?.name}
        </Typography>
        <Stack gap={3}>
          <Stack
            direction="row"
            // Use sx prop due to this issue:
            // https://github.com/mui/material-ui/issues/47844
            sx={{
              columnGap: 3,
              rowGap: 1,
            }}
            alignItems="center"
          >
            <DashboardMetaProperties dashboardData={dashboardData} />
          </Stack>
          {dashboardData.dashboardType !== 'System' && (
            <DashboardCreatedProperty dashboardData={dashboardData} />
          )}
        </Stack>
      </Stack>
      <Divider />
      <Stack direction="row" justifyContent="end">
        <Button
          component={Link}
          to={routes.reporting.dashboard(dashboardId)}
          variant="outlined"
          color="secondary"
        >
          {t('ViewDashboardButton')}
        </Button>
      </Stack>
    </Stack>
  );
};
